/**
 * Front-end admin drag-to-reposition.
 *
 * When a logged-in admin loads the front-end, the launcher gets a
 * thin drag handle that lets them reposition the widget directly on
 * the live site. On drop, the new corner + offsets are saved via the
 * authenticated REST settings endpoint.
 *
 * Non-admins never see the handle — the adminMode flag comes
 * server-side from current_user_can('manage_options').
 */

interface DragConfig {
  apiRoot: string;
  nonce: string;
}

export function enableAdminDrag(cfg: DragConfig): () => void {
  const launcher = document.querySelector<HTMLElement>('.g-launcher');
  const root = document.getElementById('grippy-widget-root');
  if (!launcher || !root) return () => {};

  root.setAttribute('data-admin-mode', '1');

  let dragging = false;
  let startX = 0;
  let startY = 0;
  let originRect: DOMRect | null = null;

  const onPointerDown = (e: PointerEvent) => {
    // Only initiate drag from a long-press or shift+click — a regular
    // tap still opens the chat as normal.
    if (!e.shiftKey) return;
    e.preventDefault();
    e.stopPropagation();
    dragging = true;
    startX = e.clientX;
    startY = e.clientY;
    originRect = launcher.getBoundingClientRect();
    launcher.setPointerCapture(e.pointerId);
    launcher.classList.add('g-launcher--dragging');
  };

  const onPointerMove = (e: PointerEvent) => {
    if (!dragging || !originRect) return;
    e.preventDefault();
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    const newLeft = originRect.left + dx;
    const newTop = originRect.top + dy;
    // Use top/left during drag so we can move freely; we'll convert
    // back to bottom/right (anchored) on drop.
    launcher.style.setProperty('top', `${newTop}px`, 'important');
    launcher.style.setProperty('left', `${newLeft}px`, 'important');
    launcher.style.setProperty('right', 'auto', 'important');
    launcher.style.setProperty('bottom', 'auto', 'important');
  };

  const onPointerUp = async (e: PointerEvent) => {
    if (!dragging) return;
    dragging = false;
    launcher.releasePointerCapture(e.pointerId);
    launcher.classList.remove('g-launcher--dragging');

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const rect = launcher.getBoundingClientRect();

    // Snap to the nearest horizontal corner — chat widgets always
    // anchor bottom-right or bottom-left, free-floating isn't useful.
    const centerX = rect.left + rect.width / 2;
    const position: 'bottom-right' | 'bottom-left' = centerX > vw / 2 ? 'bottom-right' : 'bottom-left';

    // Distance from bottom & side, clamped to the same 0-400 range
    // the backend accepts.
    const offsetBottom = Math.max(0, Math.min(400, Math.round(vh - rect.bottom)));
    const offsetSide = Math.max(
      0,
      Math.min(400, Math.round(position === 'bottom-right' ? vw - rect.right : rect.left)),
    );

    // Apply locally so the user sees the snap immediately.
    root.setAttribute('data-position', position);
    root.style.setProperty('--g-offset-bottom', `${offsetBottom}px`);
    root.style.setProperty('--g-offset-side', `${offsetSide}px`);
    launcher.style.removeProperty('top');
    launcher.style.removeProperty('left');
    launcher.style.removeProperty('right');
    launcher.style.removeProperty('bottom');

    // Persist via the authenticated settings endpoint. Silent failure
    // — drag is cosmetic, the visitor never sees the error.
    try {
      await fetch(cfg.apiRoot + 'settings', {
        method: 'PUT',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': cfg.nonce,
        },
        body: JSON.stringify({
          widget_position: position,
          widget_offset_bottom: offsetBottom,
          widget_offset_side: offsetSide,
        }),
      });
      showToast('Position saved');
    } catch {
      showToast('Could not save — check connection');
    }
  };

  // Stop the click event after a drag so the chat doesn't open on drop.
  const onClick = (e: MouseEvent) => {
    if (launcher.classList.contains('g-launcher--was-dragged')) {
      e.preventDefault();
      e.stopPropagation();
      launcher.classList.remove('g-launcher--was-dragged');
    }
  };

  launcher.addEventListener('pointerdown', onPointerDown);
  launcher.addEventListener('pointermove', onPointerMove);
  launcher.addEventListener('pointerup', onPointerUp);
  launcher.addEventListener('click', onClick, true);

  // Inject a tiny tooltip on first load so admins know about the gesture.
  if (!sessionStorage.getItem('grippy:admin-drag-hint')) {
    sessionStorage.setItem('grippy:admin-drag-hint', '1');
    showToast('Admin: shift-drag the chat bubble to reposition', 5000);
  }

  return () => {
    launcher.removeEventListener('pointerdown', onPointerDown);
    launcher.removeEventListener('pointermove', onPointerMove);
    launcher.removeEventListener('pointerup', onPointerUp);
    launcher.removeEventListener('click', onClick, true);
    root.removeAttribute('data-admin-mode');
  };
}

function showToast(message: string, durationMs = 2200): void {
  const existing = document.querySelector('.g-admin-toast');
  if (existing) existing.remove();
  const el = document.createElement('div');
  el.className = 'g-admin-toast';
  el.textContent = message;
  document.body.appendChild(el);
  setTimeout(() => el.classList.add('g-admin-toast--in'), 10);
  setTimeout(() => {
    el.classList.remove('g-admin-toast--in');
    setTimeout(() => el.remove(), 250);
  }, durationMs);
}
